import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Button, Chip, CircularProgress, Alert,
  Card, CardContent, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, useMediaQuery, useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Block as VoidIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  CalendarToday as DateIcon,
  Payment as PaymentIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { serverMessage } from '../../utils/apiConvert';
import ReceiptService from '../../services/receipt.service';
import { can } from '../../utils/roleUtils';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const ReceiptDetail = () => {
  // Session user for permission checks (this page receives no currentUser prop).
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  // Void dialog
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  useEffect(() => {
    fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    setLoading(true);
    try {
      const data = await ReceiptService.getById(id);
      setReceipt(data);
    } catch (err) {
      console.error('Error fetching receipt:', err);
      setError('Failed to load receipt details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const formatDateTime = (date) => date ? new Date(date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const handleVoidSubmit = async () => {
    if (!voidReason.trim()) return;
    setVoidSubmitting(true);
    try {
      await ReceiptService.void(receipt.id, voidReason);
      setVoidDialog(false);
      fetchReceipt();
    } catch (err) {
      setError(serverMessage(err, 'Failed to void receipt'));
      setVoidDialog(false);
      fetchReceipt();
    } finally {
      setVoidSubmitting(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress /></Box>;
  }

  if (error && !receipt) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/receipts')} size="small">Back to Receipts</Button>
      </Box>
    );
  }

  if (!receipt) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>Receipt not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/receipts')} size="small">Back to Receipts</Button>
      </Box>
    );
  }

  const isVoided = receipt.status === 'VOIDED';
  const remainingDue = receipt.invoiceAmount - receipt.amountApplied;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/receipts')} size="small" sx={{ fontSize: fontSize.sm }}>
            {!isMobile && 'Back'}
          </Button>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            {receipt.receiptNumber}
          </Typography>
          <Chip label={ReceiptService.getStatusLabel(receipt.status)} size="small"
            color={ReceiptService.getStatusColor(receipt.status)} sx={{ fontSize: fontSize.xs, height: 24 }} />
        </Box>
        <Box display="flex" gap={1}>
          {receipt.status === 'COMPLETED' && can(sessionUser, 'finance', 'write') && (
            <Button variant="outlined" color="error" size="small" startIcon={<VoidIcon sx={{ fontSize: 16 }} />}
              onClick={() => setVoidDialog(true)} sx={{ fontSize: fontSize.sm }}>
              {isMobile ? 'Void' : 'Void Receipt'}
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Voided Alert */}
      {isVoided && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<VoidIcon />}>
          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 0.5 }}>This receipt has been voided</Typography>
          <Typography sx={{ fontSize: fontSize.xs }}>
            <strong>Reason:</strong> {receipt.voidReason}
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs }}>
            <strong>Voided by:</strong> {receipt.voidedBy} on {formatDateTime(receipt.voidedAt)}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Receipt Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2 }}>Receipt Details</Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Customer</Typography>
                    {receipt.customerName && receipt.internalOrganizationId ? (
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/organizations/internal/${receipt.internalOrganizationId}`)}>
                        {receipt.customerName}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{receipt.customerName || '—'}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <DateIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Receipt Date</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatDate(receipt.date)}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <PaymentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Payment Method</Typography>
                    <Chip label={ReceiptService.getPaymentMethodLabel(receipt.paymentMethod)} size="small" variant="outlined"
                      color={ReceiptService.getPaymentMethodColor(receipt.paymentMethod)} sx={{ fontSize: fontSize.xs, height: 22, mt: 0.5 }} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <ReceiptIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Initiator</Typography>
                    <Chip label={ReceiptService.getInitiatorLabel(receipt.initiator)} size="small"
                      color={ReceiptService.getInitiatorColor(receipt.initiator)} sx={{ fontSize: fontSize.xs, height: 22, mt: 0.5 }} />
                  </Box>
                </Box>
              </Grid>
              {receipt.referenceNumber && (
                <Grid item xs={12} sm={6}>
                  <Box mb={2}>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Reference Number</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, fontFamily: 'monospace' }}>{receipt.referenceNumber}</Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Box mb={2}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Created By</Typography>
                  <Typography sx={{ fontSize: fontSize.sm }}>{[receipt.createdByName, formatDateTime(receipt.createdAt)].filter(Boolean).join(' · ')}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Invoice Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LinkIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Linked Invoice</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Invoice Number</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/invoices/${receipt.invoiceId}`)}>
                    {receipt.invoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Invoice Amount</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                    {ReceiptService.formatCurrency(receipt.invoiceAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Amount Applied</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: isVoided ? 'text.secondary' : (isDark ? '#4caf50' : '#2e7d32'), textDecoration: isVoided ? 'line-through' : 'none' }}>
                    {ReceiptService.formatCurrency(receipt.amountApplied)}
                  </Typography>
                </Grid>
                {remainingDue > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Remaining Due on Invoice</Typography>
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: isDark ? '#ef5350' : '#c62828' }}>
                        {ReceiptService.formatCurrency(remainingDue)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>

          {/* Settlement Link */}
          {receipt.settlementId && (
            <Paper sx={{ p: 3 }}>
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1 }}>Settlement Reference</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" gap={1}>
                <LinkIcon sx={{ fontSize: 18, color: 'info.main' }} />
                <Typography sx={{ fontSize: fontSize.sm, color: 'info.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/settlements/${receipt.settlementId}`)}>
                  Settlement: {receipt.referenceNumber}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>
                This receipt was auto-generated from a settlement
              </Typography>
            </Paper>
          )}
        </Grid>

        {/* Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: isMobile ? 'static' : 'sticky', top: 20 }}>
            <CardContent>
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2 }}>Payment Summary</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Status</Typography>
                <Chip label={ReceiptService.getStatusLabel(receipt.status)} size="small"
                  color={ReceiptService.getStatusColor(receipt.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Payment Method</Typography>
                <Chip label={ReceiptService.getPaymentMethodLabel(receipt.paymentMethod)} size="small" variant="outlined"
                  color={ReceiptService.getPaymentMethodColor(receipt.paymentMethod)} sx={{ fontSize: fontSize.xs, height: 22 }} />
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Initiator</Typography>
                <Chip label={ReceiptService.getInitiatorLabel(receipt.initiator)} size="small"
                  color={ReceiptService.getInitiatorColor(receipt.initiator)} sx={{ fontSize: fontSize.xs, height: 22 }} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Invoice Amount</Typography>
                <Typography sx={{ fontSize: fontSize.sm }}>{ReceiptService.formatCurrency(receipt.invoiceAmount)}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Amount Applied</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: isVoided ? 'text.secondary' : (isDark ? '#4caf50' : '#2e7d32'), textDecoration: isVoided ? 'line-through' : 'none' }}>
                  {ReceiptService.formatCurrency(receipt.amountApplied)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                  Created: {formatDateTime(receipt.createdAt)}
                </Typography>
                {receipt.createdByName && (
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                    By: {receipt.createdByName}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Void Dialog */}
      <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600, pb: 1 }}>Void Receipt</DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Receipt</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{receipt.receiptNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Invoice</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{receipt.invoiceNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Amount</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                  {ReceiptService.formatCurrency(receipt.amountApplied)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Alert severity="warning" sx={{ mb: 2, fontSize: fontSize.sm }}>
            This will refund <strong>{ReceiptService.formatCurrency(receipt.amountApplied)}</strong> back to the customer wallet and update the invoice status.
          </Alert>

          <TextField fullWidth size="small" label="Void Reason" multiline rows={3}
            value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
            required placeholder="Enter reason for voiding this receipt..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVoidDialog(false)} size="small" sx={{ fontSize: fontSize.sm }}>Cancel</Button>
          <Button variant="contained" color="error" size="small"
            onClick={handleVoidSubmit}
            disabled={voidSubmitting || !voidReason.trim()}
            sx={{ fontSize: fontSize.sm }}>
            {voidSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptDetail;
